import { View } from "@novorender/api";
import { quat, ReadonlyVec2, vec2, vec3 } from "gl-matrix";

import { radToDeg } from "utils/math";
import { isRealVec } from "utils/misc";

type Size = {
    width: number;
    height: number;
};

export function moveSvgCursor({
    svg,
    view,
    size,
    x,
    y,
    pickResult,
    color,
    overrideKind = undefined,
    canvas = null,
}: {
    svg: SVGSVGElement;
    view: View;
    size: Size;
    x: number;
    y: number;
    pickResult:
        | {
              position: vec3;
              normal: vec3;
              sampleType: "edge" | "corner" | "surface" | undefined;
              normalVS: vec3;
              x: number;
              y: number;
              objectId: number;
              deviation?: number | undefined;
              depth: number;
          }
        | undefined;
    color: string;
    overrideKind:
        | "edge"
        | "corner"
        | "surface"
        | "cross"
        | "rotatedCross"
        | "coordinatesSurface"
        | "sectionPlane"
        | undefined;
    pointSnapGuides?: (ReadonlyVec2 | undefined)[];
    canvas?: HTMLCanvasElement | null;
}) {
    if (!svg || !view) {
        return;
    }
    const g = svg.children.namedItem("cursor");
    if (!g) {
        return;
    }
    if (x < 0) {
        g.innerHTML = "";
        return;
    }
    if (overrideKind === "cross") {
        g.innerHTML = `<path d="M-10,0L10,0M0,-10L0,10" stroke-width="2" stroke-linecap="round" stroke="${
            color || "red"
        }"/>`;
    } else if (overrideKind === "rotatedCross") {
        if (color === "none") {
            g.innerHTML = "";
        } else {
            g.innerHTML = `<g transform="translate(-12 -12)">
                <path d="M6 6L18 18" stroke="#E05252" stroke-width="5" stroke-linecap="round"/>
                <path d="M18 6L6 18" stroke="#E05252" stroke-width="5" stroke-linecap="round"/>
                <path d="M6 6L18 18" stroke="#570F0F" stroke-width="2" stroke-linecap="round"/>
                <path d="M18 6L6 18" stroke="#570F0F" stroke-width="2" stroke-linecap="round"/>
            </g>`;
        }
        if (canvas) {
            canvas.style.cursor = "none";
        }
    } else if (overrideKind === "sectionPlane") {
        const angle = getSectionPlaneCursorAngle(pickResult?.normalVS, x, y, size, view);
        g.innerHTML = `<g transform="rotate(${angle.toFixed(2)})">
            <path d="M0 0L0 -28" stroke="black" stroke-opacity="0.38" stroke-width="5" stroke-linecap="round"/>
            <path d="M0 0L0 -28" stroke="#00FF33" stroke-width="2" stroke-linecap="round"/>
            <ellipse cx="0" cy="2" rx="8" ry="0.5" fill="black" fill-opacity="0.38"/>
        </g>`;
        if (canvas) {
            canvas.style.cursor = "none";
        }
    } else {
        const kind = (overrideKind ? overrideKind : pickResult?.sampleType) ?? "corner";
        const isNormalValid = pickResult?.normalVS && isRealVec(pickResult.normalVS);
        if ((kind === "surface" || kind === "coordinatesSurface") && isNormalValid) {
            const normal = vec3.clone(pickResult.normalVS);

            const { width, height } = size;
            const { camera } = view.renderState;

            if (normal[2] < 0.98) {
                const angleX = (y / height - 0.5) * camera.fov;
                const angleY = ((x - width * 0.5) / height) * camera.fov;
                vec3.transformQuat(normal, normal, quat.fromEuler(quat.create(), angleX, angleY, 0));
                let style = "";
                if (normal[2] < 1) {
                    const rot = vec3.cross(vec3.create(), normal, vec3.fromValues(0, 0, 1));
                    vec3.normalize(rot, rot);
                    const angle = (Math.acos(normal[2]) * 180) / Math.PI;
                    style = `style="transform:rotate3d(${rot[0]},${-rot[1]},${rot[2]},${angle}deg)"`;
                }
                g.innerHTML = createFeatureSvg({
                    kind,
                    style,
                    normal,
                    color,
                });
            } else {
                g.innerHTML = createFeatureSvg({
                    kind,
                    color,
                });
            }
        } else {
            if (kind === "edge") {
                g.innerHTML = `<path d="M-12,0L12,0M0,-7L0,7" stroke-width="2" stroke-linecap="round" stroke="${
                    color || "red"
                }"/>`;
            } else {
                g.innerHTML = `<path d="M-10,-10L10,10M-10,10L10,-10" stroke-width="2" stroke-linecap="round" stroke="${
                    color || "red"
                }"/>`;
            }
        }
        if (canvas) {
            canvas.style.cursor = "none";
        }
    }

    g.setAttribute("transform", `translate(${x},${y})`);
}

export function moveSvgCursorPointSnap({
    svg,
    view,
    x,
    y,
    pointSnapGuides,
    snap,
    canvas = null,
    color = "#0F3",
}: {
    svg: SVGSVGElement;
    view: View;
    x: number;
    y: number;
    pointSnapGuides?: (ReadonlyVec2 | undefined)[];
    snap?: boolean;
    canvas?: HTMLCanvasElement | null;
    color?: string;
}) {
    if (!svg || !view) {
        return;
    }
    const g = svg.children.namedItem("cursor");
    if (!g) {
        return;
    }
    let paths: string;
    if (pointSnapGuides?.length) {
        const offset = vec2.create();
        paths = pointSnapGuides
            .map((point) => {
                if (!point) {
                    return "";
                }
                vec2.scale(offset, point, 9);
                return `
                    <path d="M12 12l${offset[0]} ${offset[1]}" stroke="black" stroke-opacity="0.38" stroke-width="5" stroke-linecap="round"/>
                    <path d="M12 12l${offset[0]} ${offset[1]}" stroke="#0F3" stroke-width="2" stroke-linecap="round"/>
                `;
            })
            .join("");
    } else {
        paths = `
            <path d="M12 3V21" stroke="black" stroke-opacity="0.38" stroke-width="5" stroke-linecap="round"/>
            <path d="M12 3V21" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
            <path d="M21 12L3 12" stroke="black" stroke-opacity="0.38" stroke-width="5" stroke-linecap="round"/>
            <path d="M21 12L3 12" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
        `;
    }
    const r = snap ? 6 : 3;
    g.innerHTML = `
        <g transform="translate(-12,-12)">
            ${paths}
            <circle cx="12" cy="12" r="${r}" fill="${color}"/>
            <circle cx="12" cy="12" r="${r}" fill="#092A25" stroke="${color}" stroke-width="2"/>
        </g>
    `;
    if (canvas) {
        canvas.style.cursor = "none";
    }

    g.setAttribute("transform", `translate(${x},${y})`);
}

export function moveSvgCursorDimension({
    svg,
    view,
    x,
    y,
    snap,
    normalVS,
    size,
    isSelected = false,
    snapToSurface = true,
    canvas = null,
}: {
    svg: SVGSVGElement;
    view: View;
    x: number;
    y: number;
    snap?: boolean;
    normalVS?: vec3;
    size?: { width: number; height: number };
    isSelected?: boolean;
    snapToSurface?: boolean;
    canvas?: HTMLCanvasElement | null;
}) {
    if (!svg || !view) {
        return;
    }
    const g = svg.children.namedItem("cursor");
    if (!g) {
        return;
    }

    if (snap && isSelected) {
        const transformStyle =
            snapToSurface && normalVS && size && isRealVec(normalVS)
                ? getSurfaceProjectionTransform(normalVS, x, y, size, view)
                : "";
        const style = transformStyle ? `style="transform:${transformStyle}"` : "";

        g.innerHTML = `
            <g transform="translate(-12,-12)" ${style}>
                <rect width="20" height="20" x="2" y="2" fill="#232729" opacity="0.5" rx="4"/>
                <rect width="6" height="6" x="9" y="9" fill="#0f3" rx="2"/>
            </g>
        `;
    } else {
        g.innerHTML = `
            <g transform="translate(-12,-12)">
                <path d="M12 3V21" stroke="black" stroke-opacity="0.38" stroke-width="5" stroke-linecap="round"/>
                <path d="M12 3V21" stroke="#0F3" stroke-width="2" stroke-linecap="round"/>
                <path d="M21 12L3 12" stroke="black" stroke-opacity="0.38" stroke-width="5" stroke-linecap="round"/>
                <path d="M21 12L3 12" stroke="#0F3" stroke-width="2" stroke-linecap="round"/>
                <circle cx="12" cy="12" r="3" fill="#0F3"/>
                <circle cx="12" cy="12" r="3" fill="#092A25" stroke="#0F3" stroke-width="2"/>
            </g>
        `;
    }

    if (canvas) {
        canvas.style.cursor = "none";
    }

    g.setAttribute("transform", `translate(${x},${y})`);
}

/**
 * @returns false if cursor should be reset to default
 */
export function moveSvgCameraCursor({
    svg,
    view,
    x,
    y,
    canvas,
    buttons = 0,
    isMouse = true,
}: {
    svg: SVGSVGElement;
    view: View;
    x: number;
    y: number;
    canvas: HTMLCanvasElement | null;
    buttons?: number;
    isMouse?: boolean;
}) {
    const g = svg.children.namedItem("camera-cursor");
    if (!g) {
        return false;
    }

    if (x === -100) {
        g.innerHTML = "";
        return false;
    }

    if (view.activeController.kind === "hover" && view.controllers.hover.clickPosition) {
        if (view.controllers.hover.motion === "keepElevation") {
            const [cx, cy] = view.controllers.hover.clickPosition;
            const angle = radToDeg(Math.atan2(cy - y, cx - x)) + -90;

            const pivot = `
                <circle r="10" cx="${cx}" cy="${cy}" fill="black" stroke="white" />
                <circle r="5" cx="${cx}" cy="${cy}" stroke="white" />
            `;

            const arrow = isMouse
                ? `<path d="M-6 0h12L0 -8z" transform="translate(${x},${y}) rotate(${angle})" stroke="white" />`
                : "";
            g.innerHTML = `${pivot}${arrow}`;

            if (canvas) {
                canvas.style.cursor = "none";
            }
            return true;
        } else {
            if (canvas) {
                canvas.style.cursor = "move";
            }
            return true;
        }
    }

    const kind = view.activeController.kind;
    if (
        ((kind === "flight" || kind === "hover") && buttons === 2) ||
        ((kind === "cadRightPan" || kind === "cadMiddlePan") && buttons === 1)
    ) {
        const { pivot } = view.controllers[kind];
        if (pivot) {
            const point = view.convert.worldSpaceToScreenSpace([pivot.center], { round: false })[0];
            const pivotIcon = point ? `<circle r="3" cx="${point[0]}" cy="${point[1]}" stroke="white" />` : "";
            const cursorIcon = isMouse
                ? `<g transform="translate(${x - 12},${y - 12})">
                    <path d='M5.97683 16.6239L6.64016 17.2873C9.57094 20.218 14.3227 20.218 17.2534 17.2873C19.2263 15.3144 19.8711 12.5164 19.1879 10.0042M5.93854 19.4267V16.5983H8.76696' stroke='white'/>
                    <path d='M17.917 7.34013L17.2537 6.6768C14.3229 3.74602 9.57116 3.74602 6.64038 6.6768C4.65583 8.66135 4.0151 11.4808 4.71818 14.0045M17.9553 4.53732L17.9553 7.36575L15.1269 7.36575' stroke='white'/>
                    <circle r='3' cx='12' cy='12' fill='black' stroke='white' />
                </g>`
                : "";
            g.innerHTML = `${pivotIcon}${cursorIcon}`;
        }

        if (canvas) {
            canvas.style.cursor = "none";
        }
        return true;
    }

    g.innerHTML = "";
    return false;
}

function createFeatureSvg({
    kind,
    style,
    normal,
    color,
}: {
    kind: "edge" | "corner" | "surface" | "coordinatesSurface";
    style?: string;
    normal?: vec3;
    color?: string;
}) {
    switch (kind) {
        case "surface":
            return surfaceSvg(style, normal, color);
        case "coordinatesSurface":
            return coordinatesSurfaceSvg(style);
        default:
            return "";
    }
}

function coordinatesSurfaceSvg(style: string | undefined): string {
    return `<circle r="15" stroke="#00FF33" stroke-width="2" fill="none" ${style}/>
            <circle r="10" fill="#000000ff" ${style}/>  
            <circle r="2" fill="#00FF33" ${style}/>`;
}

function surfaceSvg(style: string | undefined, normal: number[] | undefined, color: string | undefined): string {
    if (normal === undefined) {
        return `<circle r="20" fill="rgba(255,255,255,0.25)" /><circle r="2" fill=${color} />`;
    } else {
        return `<circle r="20" fill="rgba(255,255,255,0.25)" ${style}/><line x2="${(normal[0] * 20).toFixed(1)}" y2="${(
            normal[1] * -20
        ).toFixed(1)}" stroke=${color} stroke-width="2" stroke-linecap="round" />`;
    }
}

/**
 * Computes a CSS/SVG transform string to project a surface icon so it matches the surface normal at a given screen position.
 * Returns a string like 'rotate3d(x, y, z, angle)' or an empty string if no rotation is needed.
 *
 * @param normalVS - The surface normal in view space (vec3)
 * @param x - Screen x position (pixels)
 * @param y - Screen y position (pixels)
 * @param size - { width, height } of the canvas
 * @param view - The view object (from the rendering context)
 */
export function getSurfaceProjectionTransform(
    normalVS: vec3,
    x: number,
    y: number,
    size: { width: number; height: number },
    view: View,
): string {
    if (!normalVS || normalVS.length !== 3) {
        return "";
    }
    const { camera } = view.renderState;
    const normal = vec3.clone(normalVS);
    if (normal[2] < 0.98) {
        const angleX = (y / size.height - 0.5) * camera.fov;
        const angleY = ((x - size.width * 0.5) / size.height) * camera.fov;
        vec3.transformQuat(normal, normal, quat.fromEuler(quat.create(), angleX, angleY, 0));
        if (normal[2] < 1) {
            const rot = vec3.cross(vec3.create(), normal, vec3.fromValues(0, 0, 1));
            vec3.normalize(rot, rot);
            const angle = (Math.acos(normal[2]) * 180) / Math.PI;
            return `rotate3d(${rot[0]},${-rot[1]},${rot[2]},${angle}deg)`;
        }
    }
    return "";
}

export function getLabelPixelWidth(
    label: string,
    fontSize: number = 12,
    padding: number = 20,
    fontPixelSizeFactor: number = 1,
) {
    // Estimate: monospace font, each character ~0.6 * fontSize px wide
    const charWidth = fontSize * fontPixelSizeFactor;
    return Math.ceil(label.length * charWidth + padding);
}

export function getRoundedRectPath(width: number, height: number, radius: number) {
    return `M0 ${radius}C0 ${radius / 2} ${radius / 2} 0 ${radius} 0H${width - radius}C${width - radius / 2} 0 ${width} ${radius / 2} ${width} ${radius}V${height - radius}C${width} ${height - radius / 2} ${width - radius / 2} ${height} ${width - radius} ${height}H${radius}C${radius / 2} ${height} 0 ${height - radius / 2} 0 ${height - radius}V${radius}Z`;
}

function getSectionPlaneCursorAngle(
    normalVS: vec3 | undefined,
    x: number,
    y: number,
    size: { width: number; height: number },
    view: View,
): number {
    if (!normalVS || !isRealVec(normalVS)) {
        return 0;
    }
    const normal = vec3.clone(normalVS);

    // Correct perspective
    if (normal[2] < 0.98) {
        const { camera } = view.renderState;
        const angleX = (y / size.height - 0.5) * camera.fov;
        const angleY = ((x - size.width * 0.5) / size.height) * camera.fov;
        vec3.transformQuat(normal, normal, quat.fromEuler(quat.create(), angleX, angleY, 0));
    }

    const nx = normal[0];
    const ny = normal[1];
    const len2d = Math.sqrt(nx * nx + ny * ny);
    if (len2d < 0.01) {
        // Normal points almost straight at the camera
        return 0;
    }

    return (Math.atan2(nx, ny) * 180) / Math.PI;
}
